import { Model } from '@viddsee/server-api-client';
import { ContentCardCompat, SimpleContentCompat } from './ContentCardCompat';
import { ActionableCardCompat } from './ActionableCardCompat';
import { MediaLikedCardCompat } from './MediaLikedCardCompat';
import { UtmParameters } from '@/scripts/types/UtmParameters';

export interface VideoInfoCardFooterTag extends ActionableCardCompat {
  id: string;
  name: string;
}

export interface VideoInfoCardCompat extends ContentCardCompat, SimpleContentCompat, ActionableCardCompat, MediaLikedCardCompat {
  id: string;
  isFeatured: boolean;
  likes: string;
  duration: number;
  tags: VideoInfoCardFooterTag[];
}

export const formatTag = (tagType: string) => (tags?: string[]): VideoInfoCardFooterTag[] => (
  (tags || [])
    .map(x => x.trim())
    .map(name => ({
      id: `${tagType}-${name}`,
      name,
      url: `/${tagType}/${name}`,
    }))
);
export const makeFooterGenre = (videoInfo?: Model.Video.Info): VideoInfoCardFooterTag[] => {
  if (videoInfo == null) return [];

  return [
    ...formatTag('genre')(videoInfo.genres),
  ];
};
export const makeFooterTopic = (videoInfo?: Model.Video.Info): VideoInfoCardFooterTag[] => {
  if (videoInfo == null) return [];

  return [
    ...formatTag('topic')(videoInfo.topics),
  ];
};

export const makeLikes = (videoInfo?: Model.Video.Info): string => {
  if (!videoInfo) return '';

  return (videoInfo.likeCount >= 1000)
    ? `${(videoInfo.likeCount/1000.0).toFixed(1)}k`
    : `${videoInfo.likeCount}`
  ;
};

export const makeReviewScore = (videoInfo?: Model.Video.Info): string => {
  if (!videoInfo || !videoInfo.reviewScore) return '';

  return `${videoInfo.reviewScore?.toFixed(1)}/5`;
};

export const makeUrl = (videoInfo?: Model.Video.Info, utmParameters?: UtmParameters): string => {
  if (!videoInfo) return '';

  let url = `/video/${videoInfo.slug}/${videoInfo.id}`;

  if (utmParameters) {
    const parameters = [];
    if (utmParameters.source) parameters.push(`utm_source=${encodeURIComponent(utmParameters.source)}`);
    if (utmParameters.medium) parameters.push(`utm_medium=${encodeURIComponent(utmParameters.medium)}`);
    if (utmParameters.campaign) parameters.push(`utm_campaign=${encodeURIComponent(utmParameters.campaign)}`);
    // if (utmParameters.term) parameters.push(`utm_source=${utmParameters.term}`);
    // if (utmParameters.content) parameters.push(`utm_source=${utmParameters.content}`);
    const queryString = parameters.join('&');
    url = `${url}?${queryString}`;
  }

  return url;
};

export const getUnavailableVideoInfoCard = (): VideoInfoCardCompat => ({
  id: `${Math.random() * 100000}`,
  isFeatured: false,
  title: 'Video is unavailable',
  description: '',
  imageUrl: '/images/video/video-unavailable.png',
  url: 'javascript:void(0)',
  likes: '',
  duration: 0,
  tags: [],
  genres: [],
  languages: [],
  rating: '',
  reviewScore: '',
});

export const getEmptyVideoInfoCard = (): VideoInfoCardCompat => ({
  id: '',
  isFeatured: false,
  title: '',
  description: '',
  imageUrl: '',
  url: 'javascript:void(0)',
  likes: '',
  duration: 0,
  tags: [],
  genres: [],
  languages: [],
  rating: '',
  reviewScore: '',
});

export const makeVideoInfoCardCompat = (videoInfo?: Model.Video.Info, utmParameters?: UtmParameters): VideoInfoCardCompat => {
  if (videoInfo == null) {
    return getEmptyVideoInfoCard();
  }
  if ('isUnavailable' in videoInfo) {
    return getUnavailableVideoInfoCard();
  }

  return {
    id: videoInfo.id,
    isFeatured: videoInfo.isFeatured,
    title: videoInfo.title,
    description: videoInfo.headline || videoInfo.description,
    imageUrl: 'imageUrl' in videoInfo ? videoInfo['imageUrl'] : videoInfo.photoMediumUrl,
    url: makeUrl(videoInfo, utmParameters),
    likes: makeLikes(videoInfo),
    duration: videoInfo.duration,
    tags: [...makeFooterGenre(videoInfo), ...makeFooterTopic(videoInfo)],
    genres: makeFooterGenre(videoInfo),
    languages: videoInfo.languages,
    rating: videoInfo.regionContentRating?.name,
    reviewScore: makeReviewScore(videoInfo),
  };
};
